import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Space } from 'tdesign-react';

import { CardFormComponent, IFormItemProps } from '@/components';
import { PageIds } from '@/const/tip';
import { AiImageModelType, AiImageReferenceType, AiPrompt, DEFAULT_AI_IMAGE_MODEL } from '@/interface/aigc';
import { AigcImageDialogProps } from '@/material/AigcImageDialog';
import { filterForm } from '@/utils/formFilter';
import { orgConfigs } from '@/utils/orgFormConfig';

import { ImageInfo } from './custom/customImage';
import { aigcImageFormCard1 } from './form-config';
import styles from './index.module.less';

interface AigcImageInfoProps {
  data: AigcImageDialogProps['data'];
  getPrompt?: (prompt: string) => void;
  onModelChange?: (model: AiImageModelType) => void;
}

export interface AigcImageInfoRef {
  submitForm: () => void;
  getImageInfo: () => any;
}

interface IFormData {
  model?: AiImageModelType;
  text?: AiPrompt;
  ratio?: {
    width: number;
    height: number;
  };
  style?: string;
  cref?: ImageInfo;
  cw?: number;
  sref?: ImageInfo;
  sw?: number;
  negativeText?: string;
}

const AigcImageInfo = forwardRef<AigcImageInfoRef, AigcImageInfoProps>((props, ref) => {
  const { data, getPrompt } = props;
  const cardFormRef1 = useRef<any>(null);
  const initData: IFormData = {
    model: DEFAULT_AI_IMAGE_MODEL,
    text: { prompt: data?.promptText },
    ratio: {
      width: 576,
      height: 1024,
    },
    style: data?.style,
    cw: 100,
    sw: 100,
    negativeText: '',
  };
  const [formData, setFormData] = useState<IFormData>(initData);

  useEffect(() => {
    setFormData({ ...formData, text: { prompt: data?.promptText } });
    getPrompt?.(data?.promptText);
  }, [data?.promptText]);

  useEffect(() => {
    if (data?.style) {
      setFormData({ ...formData, style: data?.style });
      cardFormRef1.current?.formRef?.form?.setFieldsValue({ style: data?.style });
    }
  }, [data?.style]);

  useImperativeHandle(ref, () => ({
    submitForm: handleSave,
    getImageInfo: () => ({ ...formData }),
  }));

  const onSubmit = async (values: IFormData) => {
    setFormData(values);
  };

  const handleSave = () => {
    onSubmit(formData);
  };

  const onValuesChange = useCallback((content: IFormData) => {
    const newData = { ...formData, ...content };
    getPrompt?.(newData.text?.prompt || '');
    setFormData(newData);
  }, [formData]);

  const refactorFieldsConfig = (config: IFormItemProps[], values: IFormData) => {
    const tempConfigs = orgConfigs(config, values, {});
    const filterValue = { model: values?.model };

    return tempConfigs?.map((e) => {
      if (!filterForm(e.filter, filterValue)) return undefined;

      if (e.props === 'text') {
        // 即梦3.1模型：禁用上传功能
        if (values.model === AiImageModelType.AI_IMAGE_JIMENG_31) {
          return {
            ...e,
            uiOptions: {
              ...e.uiOptions,
              disabledUpload: true,
            },
          };
        }
        // // 方舟4.0模型：仅支持智能参考，不显示参考对话框
        // if (values.model === AiImageModelType.AI_IMAGE_FANGZHOU_40) {
        //   return {
        //     ...e,
        //     uiOptions: {
        //       ...e.uiOptions,
        //       referOptions: [AiImageReferenceType.AUTO],
        //       hideReferDialog: true,
        //     },
        //   };
        // }
      }

      if (e.props === 'model') {
        const hasReferImage = Boolean(values.text?.image?.url?.url);
        const isAdvancedRefer = values.text?.referenceType === AiImageReferenceType.CW || values.text?.referenceType === AiImageReferenceType.SW;
        const options = e.uiOptions?.options?.map((opt: any) => {
          if (hasReferImage && opt.value === AiImageModelType.AI_IMAGE_JIMENG_31) {
            // 已上传参考图时，不支持即梦3.1模型
            return {
              ...opt,
              disabled: true,
              title: '参考图不支持灵动版模型',
            };
          }
          if (isAdvancedRefer && opt.value !== AiImageModelType.AI_IMAGE_JIMENG_30) {
            // 设置风格/形象参考时，仅支持即梦3.0模型
            return {
              ...opt,
              disabled: true,
              title: '风格/形象参考仅支持经典版模型',
            };
          }
          return opt;
        });

        return {
          ...e,
          uiOptions: {
            ...e.uiOptions,
            options,
          },
        };
      }

      return e;
    }).filter(e => e !== undefined) as IFormItemProps[];
  };

  return (
    <div id="nova-edit-container" className={styles.container} >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <CardFormComponent
          cRef={cardFormRef1}
          key={'aigc-image-1'}
          title={''}
          pageId={PageIds.AIGC_INFO}
          formData={formData}
          fieldsConfig={aigcImageFormCard1.fieldsConfig}
          formConfig={{
            layout: 'vertical',
            labelWidth: '100%',
            labelAlign: 'top',
            onValuesChange,
          }}
          onSubmit={onSubmit}
          extendControlConfig={{
            refactorFieldsConfig: config => refactorFieldsConfig(config, formData),
          }}
        />
      </Space>
    </div>
  );
});

export default AigcImageInfo;
